import { z } from "zod";
import {
  canEdit,
  EARLY_COPY_FIELD,
  holds,
  type Item,
  isOnTeam,
  itemOf,
  itselfOf,
  type Team,
} from "./access";
import {
  applyOp,
  type BaseRecords,
  collectionOf,
  DraftOp,
  type Entry,
  type Fields,
  GEOMETRY_FIELDS,
  generationOf,
  idOf,
  PAGE_KEY,
  RecordKey,
  recordOf,
  type StructuralOp,
  textAt,
  writeText,
} from "./records";
import { fromBase64, isTextUpdate, type SavedText, TextDocs } from "./texts";

/** The revision each field of each record must still have: `$` for the record itself. */
export const Expect = z.record(RecordKey, z.record(z.string(), z.number().int().nonnegative()));
export type Expect = z.infer<typeof Expect>;

/** One finished action from one editor: everything it changed, applied whole or not at all. */
export const DraftCommit = z.strictObject({
  /** Chosen by the editor, so its answer can be matched to it and a resend recognised. */
  id: z.string().min(8).max(64),
  /** What it did, in a few words: "Hand Liège to German Empire". */
  label: z.string().max(120),
  ops: z.array(DraftOp).min(1).max(4000),
  /**
   * The revisions it must still find. A commit is refused when any has moved on, but an undo or
   * redo leaves out a field someone else has written since, and is refused only when a record it
   * replaces, or geometry it moves back, has moved on.
   */
  expect: Expect.optional(),
  /** Whether it's an editor's undo or redo of their own change. */
  undo: z.boolean().optional(),
});
export type DraftCommit = z.infer<typeof DraftCommit>;

/** Who is changing a draft, and the team that says what they may change. */
export interface Editor {
  userId: string;
  team: Team;
}

/** What recolouring an entity changes on what's of it. */
const COLOUR_FIELDS = new Set(["fill", "symbol", "markColor", "backgroundColor", "stroke"]);
/** What renaming an entity changes on what's of it. */
const NAME_FIELDS = new Set(["name", "text"]);
/** What taking an entity away changes on what was of it. */
const OF_FIELDS = new Set(["of"]);
/** What a recommendation that lets go of what's taken away can change as it does. */
const LETTING_GO_FIELDS = new Set(["perspective", "entity", "name", "color"]);
const THINGS_FIELDS = new Set(["things"]);
const BODY_FIELDS = new Set(["body"]);
/** The map whose things a reference names when it doesn't say. */
const SPECTATOR = "spectator";

const onlyFields = (fields: Fields, allowed: ReadonlySet<string>) =>
  Object.keys(fields).every(field => allowed.has(field));
const isDrawing = (value: Fields) =>
  value.kind === "points" || value.kind === "line" || value.kind === "area";
/** The part of an id after its map or skill: `region:257` of `spectator/region:257`. */
const ownIdOf = (id: string) => id.slice(id.indexOf("/") + 1);

/** JSON with every object's keys in one order, so two equal values always compare equal. */
const canonical = (value: unknown) =>
  JSON.stringify(value ?? null, (_, inner) =>
    inner && typeof inner === "object" && !Array.isArray(inner)
      ? Object.fromEntries(Object.entries(inner).sort(([p], [q]) => p.localeCompare(q)))
      : inner,
  );

/** A field an undo or redo left alone, because someone else had changed it since. */
export interface Skipped {
  key: string;
  field: string;
}

export type Outcome =
  | { ok: true; revision: number; ops: DraftOp[]; skipped: Skipped[] }
  | { ok: false; reason: string };

/**
 * Why a draft can't keep a record as a commit would leave it, in words for its editors, or null
 * when it can. A null record is one the commit removes.
 */
export type RecordCheck = (key: string, record: Fields | null) => string | null;

/**
 * A commit's ops applied at a revision: what a draft session does with a commit it takes, and what
 * every editor does with each `committed` frame, so they all end up holding the same thing.
 */
export function applyCommitted(
  entries: Map<string, Entry>,
  base: BaseRecords,
  texts: TextDocs,
  ops: readonly DraftOp[],
  revision: number,
): void {
  for (const op of ops) {
    if (op.type !== "text") {
      applyOp(entries, op, revision);
      continue;
    }
    const initial = textAt(recordOf(op.key, entries, base), op.path) ?? "";
    const doc = texts.docOf(op.key, op.path, generationOf(entries.get(op.key), op.path), initial);
    writeText(entries, base, op.key, op.path, texts.merge(doc, fromBase64(op.update)), revision);
  }
}

/**
 * The ops that put back every record these ops touch as the draft holds it now, for an editor to
 * keep as the undo of its own change. A record they only update gets its fields' values back and
 * nothing else, so undoing it leaves others' changes to its other fields alone. Typing is undone in
 * its own text.
 */
export function inverseOf(
  ops: readonly DraftOp[],
  entries: ReadonlyMap<string, Entry>,
  base: BaseRecords,
): StructuralOp[] {
  const inverse: StructuralOp[] = [];
  const structural = ops.filter((op): op is StructuralOp => op.type !== "text");
  for (const key of new Set(structural.map(op => op.key))) {
    const own = structural.filter(op => op.key === key);
    const entry = entries.get(key);
    if (own.every(op => op.type === "update")) {
      const record = recordOf(key, entries, base) ?? {};
      const fields: Fields = {};
      for (const op of own) {
        for (const field of Object.keys(op.fields)) fields[field] = record[field] ?? null;
      }
      inverse.push({ type: "update", key, fields });
    } else if (!entry) {
      inverse.push({ type: "reset", key });
    } else if (entry.value === null) {
      inverse.push({ type: "remove", key });
    } else if (entry.whole) {
      inverse.push({ type: "set", key, value: entry.value });
    } else {
      inverse.push({ type: "reset", key }, { type: "update", key, fields: entry.value });
    }
  }
  return inverse;
}

/** The revisions an undo of these ops must still find, once the draft holds them. */
export function expectFor(ops: readonly DraftOp[], entries: ReadonlyMap<string, Entry>): Expect {
  const expect: Expect = {};
  for (const op of ops) {
    if (op.type === "text") continue;
    const revisions = entries.get(op.key)?.revisions ?? {};
    expect[op.key] ??= {};
    const fields = expect[op.key] as Record<string, number>;
    if (op.type === "update") {
      for (const field of Object.keys(op.fields)) fields[field] = revisions[field] ?? 0;
    } else {
      fields.$ = revisions.$ ?? 0;
    }
  }
  return expect;
}

/**
 * A draft being edited: what it began with, every record changed since, field by field, and the
 * texts typed into. It checks each commit against who sent it, what the draft holds now and what
 * each record may be, then applies it whole or not at all. A draft session keeps one and saves
 * what each commit changed.
 */
export class DraftRoom {
  revision: number;
  readonly entries: Map<string, Entry>;
  readonly texts = new TextDocs();

  constructor(
    readonly base: BaseRecords,
    state: {
      revision?: number;
      entries?: Iterable<[string, Entry]>;
      texts?: Iterable<SavedText>;
    } = {},
    private readonly check: RecordCheck = () => null,
  ) {
    this.revision = state.revision ?? 0;
    this.entries = new Map(state.entries);
    for (const saved of state.texts ?? []) this.texts.load(saved);
  }

  /** A record as the draft has it now, or null when it has none. */
  record(key: string): Fields | null {
    return recordOf(key, this.entries, this.base);
  }

  /** An editor's commit, taken if the team lets them make every part of it and the draft can. */
  commit(editor: Editor, commit: DraftCommit): Outcome {
    if (!isOnTeam(editor.team, editor.userId)) {
      return { ok: false, reason: "You aren't on the preset's team." };
    }
    const following = this.consequencesOf(commit.ops);
    for (const [index, op] of commit.ops.entries()) {
      const reason = this.rightsRefusal(op, editor, following.has(index));
      if (reason) return { ok: false, reason };
    }
    const replaced = new Set(
      commit.ops.filter(op => op.type !== "update" && op.type !== "text").map(op => op.key),
    );
    const skipped: Skipped[] = [];
    for (const [key, fields] of Object.entries(commit.expect ?? {})) {
      const revisions = this.entries.get(key)?.revisions ?? {};
      for (const [field, revision] of Object.entries(fields)) {
        if ((revisions[field] ?? 0) === revision) continue;
        if (!commit.undo || replaced.has(key) || GEOMETRY_FIELDS.has(field)) {
          return { ok: false, reason: "Someone else has changed it since." };
        }
        skipped.push({ key, field });
      }
    }
    const ops = commit.ops.flatMap((op): DraftOp[] => {
      if (op.type !== "update") return [op];
      const left = Object.entries(op.fields).filter(
        ([field]) => !skipped.some(skip => skip.key === op.key && skip.field === field),
      );
      return left.length ? [{ ...op, fields: Object.fromEntries(left) }] : [];
    });
    if (!ops.length) return { ok: false, reason: "Someone else has changed all of it since." };
    const reason = this.refusalOf(ops);
    if (reason) return { ok: false, reason };
    const revision = this.revision + 1;
    applyCommitted(this.entries, this.base, this.texts, ops, revision);
    this.revision = revision;
    return { ok: true, revision, ops, skipped };
  }

  /** An item as a refusal names it. */
  private named(item: Item): string {
    if (item.kind === "page") return "the page";
    if (item.kind === "recommendation") return "the lobby";
    const name = this.record(itselfOf(item))?.name;
    return typeof name === "string" && name.trim() ? name : item.id;
  }

  /**
   * Why an op isn't the editor's to make, or null when it is: changing an item, adding to it and
   * deleting it all take a role that may edit it. What follows from the rest of the commit takes
   * nothing of its own.
   */
  private rightsRefusal(op: DraftOp, { team, userId }: Editor, following: boolean): string | null {
    const item = itemOf(op.key);
    if (!item || following) return null;
    if (!canEdit(team, userId, op.key)) {
      return op.type === "remove" && op.key === itselfOf(item)
        ? `You can't delete ${this.named(item)}.`
        : `You can't edit ${this.named(item)}.`;
    }
    if (this.changesEarlyCopy(op) && !holds(team, userId, "publish")) {
      return "You can't change whether players may have early copies.";
    }
    return null;
  }

  /** Whether an op would change whether players may have early copies, which takes `publish`. */
  private changesEarlyCopy(op: DraftOp): boolean {
    if (op.key !== PAGE_KEY) return false;
    const now = this.record(PAGE_KEY)?.[EARLY_COPY_FIELD];
    switch (op.type) {
      case "update":
        return EARLY_COPY_FIELD in op.fields && op.fields[EARLY_COPY_FIELD] !== now;
      case "create":
      case "set":
        return op.value[EARLY_COPY_FIELD] !== now;
      case "reset":
        return this.base.get(PAGE_KEY)?.[EARLY_COPY_FIELD] !== now;
      default:
        return false;
    }
  }

  /**
   * A thing as it was before a commit: its own record, or, on a map kept over another, the one that
   * map began with, which is what a map kept over it holds until it changes it.
   */
  private thingBefore(key: string): Fields | null {
    const own = this.record(key);
    if (own || this.entries.has(key)) return own;
    const id = idOf(key);
    const over = this.record(`perspective:${id.slice(0, id.indexOf("/"))}`)?.over;
    return typeof over === "string"
      ? (this.base.get(`thing:${over}/${ownIdOf(id)}`) ?? null)
      : null;
  }

  /**
   * The ops of a commit that follow from the rest of it, and so need no rights of their own. Each
   * is only ever that much, so nothing else can be changed that way:
   *
   * - A map changed carries what skills refer to on it: each reference to its things follows them,
   *   is kept as a copy of what's gone, or goes, and a skill's text that named one that went names
   *   it in words.
   * - A perspective or entity taken away is let go of by what stood for it in the lobby.
   * - An entity's new colour or name is followed by what's of it on every map, and an entity taken
   *   away is let go of there too.
   */
  private consequencesOf(ops: readonly DraftOp[]): Set<number> {
    const following = new Set<number>();
    const maps = new Set<string>();
    const goneFoundations = new Set<string>();
    const goneEntities = new Set<string>();
    const recoloured = new Set<string>();
    const renamed = new Set<string>();
    for (const op of ops) {
      const collection = collectionOf(op.key);
      if (collection === "thing") maps.add(itemOf(op.key)?.id ?? "");
      if (op.type === "remove" && collection === "perspective") goneFoundations.add(idOf(op.key));
      if (op.type === "remove" && collection === "entity") goneEntities.add(idOf(op.key));
      if (op.type === "update" && collection === "entity") {
        if ("color" in op.fields) recoloured.add(idOf(op.key));
        if ("name" in op.fields) renamed.add(idOf(op.key));
      }
    }

    /** Whether the commit carries a skill's references to things on a map it changes. */
    const carriesReferencesOf = (skill: string) =>
      ops.some(op => {
        if (collectionOf(op.key) !== "reference" || itemOf(op.key)?.id !== skill) return false;
        const before = this.record(op.key);
        return before?.kind === "things" && maps.has(String(before.perspective ?? SPECTATOR));
      });

    const goneReferences = new Map<string, string[]>();
    for (const [index, op] of ops.entries()) {
      if (collectionOf(op.key) !== "reference" || !maps.size || op.type === "text") continue;
      const before = this.record(op.key);
      const skill = itemOf(op.key)?.id ?? "";
      const drawn = (op.type === "set" || op.type === "create") && isDrawing(op.value);
      const carried =
        op.type === "remove" ||
        (op.type === "update" && onlyFields(op.fields, THINGS_FIELDS)) ||
        drawn;
      const follows = before
        ? carried && before.kind === "things" && maps.has(String(before.perspective ?? SPECTATOR))
        : drawn && carriesReferencesOf(skill);
      if (!follows) continue;
      following.add(index);
      if (op.type === "remove") {
        goneReferences.set(skill, [...(goneReferences.get(skill) ?? []), op.key]);
      }
    }

    for (const [index, op] of ops.entries()) {
      if (op.type !== "update" && op.type !== "set") continue;
      const collection = collectionOf(op.key);
      if (collection === "skill" && op.type === "update" && onlyFields(op.fields, BODY_FIELDS)) {
        const gone = goneReferences.get(idOf(op.key)) ?? [];
        const before = this.record(op.key);
        if (!gone.length || typeof before?.body !== "string") continue;
        let words = before.body;
        for (const key of gone) {
          const name = this.record(key)?.name;
          words = words.replaceAll(`{{${ownIdOf(idOf(key))}}}`, String(name ?? ""));
        }
        if (words === op.fields.body) following.add(index);
      } else if (collection === "recommendation" && op.type === "update") {
        if (!onlyFields(op.fields, LETTING_GO_FIELDS)) continue;
        const before = this.record(op.key);
        const letsGoOf = (field: "perspective" | "entity", gone: Set<string>) =>
          field in op.fields &&
          op.fields[field] === null &&
          gone.has(String(before?.[field] ?? ""));
        if (letsGoOf("perspective", goneFoundations) || letsGoOf("entity", goneEntities)) {
          following.add(index);
        }
      } else if (collection === "thing") {
        const before = this.thingBefore(op.key);
        const changed =
          op.type === "update"
            ? Object.keys(op.fields)
            : before
              ? [...new Set([...Object.keys(before), ...Object.keys(op.value)])].filter(
                  field => canonical(before[field]) !== canonical(op.value[field]),
                )
              : null;
        if (!changed) continue;
        const of = Array.isArray(before?.of) ? before.of.map(String) : [];
        const ofAny = (entities: Set<string>) => of.some(id => entities.has(id));
        const only = (allowed: Set<string>) => changed.every(field => allowed.has(field));
        if (
          (ofAny(recoloured) && only(COLOUR_FIELDS)) ||
          (ofAny(renamed) && only(NAME_FIELDS)) ||
          (ofAny(goneEntities) && only(OF_FIELDS))
        ) {
          following.add(index);
        }
      }
    }
    return following;
  }

  /** Why the draft can't take these ops as it stands, or null when it can. */
  private refusalOf(ops: readonly DraftOp[]): string | null {
    /** The entry each key has after the ops so far. */
    const touched = new Map<string, Entry>();
    const entryOf = (key: string) => touched.get(key) ?? this.entries.get(key);
    for (const op of ops) {
      const current = entryOf(op.key);
      const own = new Map<string, Entry>(current ? [[op.key, current]] : []);
      const record = recordOf(op.key, own, this.base);
      if (op.type === "text") {
        if (touched.has(op.key)) return "A commit can't type into a record it also changes.";
        if (op.since !== generationOf(current, op.path)) {
          return "Someone replaced that text since.";
        }
        if (textAt(record, op.path) === undefined) return "There's no text there to type into.";
        if (!isTextUpdate(fromBase64(op.update))) return "That isn't typing the draft can merge.";
        continue;
      }
      if ((op.type === "create" || op.type === "set") && collectionOf(op.key) === "thing") {
        const id = idOf(op.key).slice(idOf(op.key).indexOf("/") + 1);
        if (typeof op.value.kind !== "string" || op.value.id !== id) {
          return "A thing needs a kind, and the id its key names.";
        }
      }
      const exists = record !== null;
      if (op.type === "create" && exists) return `Someone has made ${idOf(op.key)} already.`;
      if (op.type === "update" && !exists) return "Someone else has deleted it.";
      if (op.type === "remove" && !exists) return "It's gone already.";
      applyOp(own, op, this.revision + 1);
      touched.set(op.key, own.get(op.key) as Entry);
    }
    for (const [key, entry] of touched) {
      const problem = this.check(key, recordOf(key, new Map([[key, entry]]), this.base));
      if (problem) return problem;
    }
    return null;
  }
}
